import { picoapp } from 'picoapp'

import { instantSearch, instantSearchQuery } from 'Assets/js/instant-search'
import {
  productCardAction,
  productCardQuickShop,
  productCardSwatches,
} from 'Assets/js/product-card'

const globalComponents = {
  accordion: SAXX.Init.Component((node) => {
    const button = node.querySelector('.accordion__button')
    const panel = node.querySelector('.accordion__panel')
    const siblings = () => {
      // Setup siblings array and get the first sibling
      const siblings = []
      let sibling = node.parentNode.firstChild

      // Loop through each sibling and push to the array
      while (sibling) {
        if (sibling.nodeType === 1 && sibling !== node) {
          siblings.push(sibling)
        }
        sibling = sibling.nextSibling
      }

      return siblings
    }

    if (button) {
      button.addEventListener('click', (e) => {
        e.preventDefault()
        if (node.parentNode.getAttribute('data-close-siblings') === 'true') {
          siblings().forEach((e, i) => {
            const btn = e.querySelector('.accordion__button')
            if (btn) {
              const thisPanel = e.querySelector('.accordion__panel')
              const isExpanded = btn.getAttribute('aria-expanded')

              if (isExpanded) {
                btn.setAttribute('aria-expanded', false)
                thisPanel.hidden = true
              }
            }
          })
        }

        const isExpanded =
          button.getAttribute('aria-expanded') === 'true' || false

        button.setAttribute('aria-expanded', `${!isExpanded}`)
        panel.hidden = isExpanded
      })
    }
  }),
  carousel: SAXX.Init.Component((node) => {
    new SAXX.Library.Carousel(node, {
      autoPlay: Number(node.getAttribute('data-rotation-speed')) || null,
      initial: Number(node.getAttribute('data-initial')),
      interactionDelay:
        Number(node.getAttribute('data-interaction-delay')) || 10,
      scrollBehaviour: node.getAttribute('data-scroll-behaviour') ?? 'smooth',
    })
  }),
  countdownTimer: SAXX.Init.Component((node) => {
    new SAXX.Library.CountdownTimer(node, {
      endDate: node.getAttribute('data-end-date'),
      countdownMessage: node.getAttribute('data-countdown-message'),
      fallbackMessage: node.getAttribute('data-fallback-message'),
    })
  }),
  deferredMedia: SAXX.Init.Component((node) => {
    const loadAndPlayVideo = () => {
      const content = document.createElement('div')
      content.appendChild(
        node
          .querySelector('template')
          .content.firstElementChild.cloneNode(true),
      )

      const deferredElement = node.appendChild(
        content.querySelector('video, model-viewer, iframe'),
      )
      node.classList.add('loaded')

      if (
        deferredElement.nodeName === 'VIDEO' &&
        deferredElement.getAttribute('autoplay')
      ) {
        deferredElement.play()
      }
    }

    node
      .querySelector('[id^="Deferred-Media--Poster-"]')
      .addEventListener('click', loadAndPlayVideo, { once: true })
  }),
  drawer: SAXX.Init.Component((node, ctx) => {
    const { firstNode } = SAXX.Library.FocusTrap(node)
    const nodeId = node.getAttribute('data-drawer-id')

    node.querySelectorAll('[data-drawer-close]').forEach((ele) => {
      ele.addEventListener('click', (e) => {
        e.preventDefault()
        ctx.emit(SAXX.Events.DrawerControlInteraction)
      })
    })

    ctx.on(
      [
        SAXX.Events.DrawerControlInteraction,
        SAXX.Events.ModalControlInteraction,
      ],
      (state, details) => {
        if (!details?.controlElement || nodeId !== details?.controlElement) {
          node.toggleAttribute('inert', true)
          node.setAttribute('aria-expanded', false)

          state.restoreFocus?.focus({ preventScroll: true })
          return
        }

        const isAlreadyOpen = !!JSON.parse(node.getAttribute('aria-expanded'))
        const shouldFocus = !JSON.parse(node.getAttribute('aria-expanded'))

        node.toggleAttribute('inert', false)
        node.setAttribute('aria-expanded', true)

        if (shouldFocus && !isAlreadyOpen) {
          ctx.hydrate({ restoreFocus: document.activeElement })
          firstNode.focus({ preventScroll: true })
        }
      },
    )

    ctx.on(SAXX.Events.CustomizerSelectEvent, (_, details) => {
      if (node.getAttribute('data-section-id') === details.sectionId) {
        node.toggleAttribute('inert', false)
        node.setAttribute('aria-expanded', true)
      }
    })

    ctx.on(SAXX.Events.CustomizerDeselectEvent, (_, details) => {
      if (node.getAttribute('data-section-id') === details.sectionId) {
        node.toggleAttribute('inert', true)
        node.setAttribute('aria-expanded', false)
      }
    })

    node.addEventListener('keydown', (e) => {
      if (JSON.parse(node.getAttribute('aria-expanded')) === false) return

      const isEscPressed = e.key === 'Escape' || e.keyCode === 27
      if (!isEscPressed) return

      ctx.emit(SAXX.Events.DrawerControlInteraction)
    })
  }),
  flipTile: SAXX.Init.Component((node) => {
    node.querySelectorAll('.trigger').forEach((ele) => {
      ele.addEventListener('click', () => {
        node.classList.toggle('active')
      })
    })
  }),
  formValidation: SAXX.Init.Component((node) => {
    const formElements = Array.from(node.elements)
    const setErrorState = (ele) => {
      if (ele.type === 'submit') return

      Array.from(ele.parentNode.querySelectorAll('.text-help')).forEach(
        (message) => message.parentNode.removeChild(message),
      )

      if (ele.validity.valid) {
        ele.parentNode.classList.remove('has-danger')
        return
      }

      ele.parentNode.classList.add('has-danger')

      let message = SAXX.Localization.Required
      if (ele.validity.typeMismatch || ele.validity.patternMismatch) {
        message = SAXX.Localization.FormattingError
      }

      const name = ele.getAttribute('data-label')
      ele.parentNode.insertAdjacentHTML(
        'beforeend',
        `<small class="text-help">'${name}' ${message}</small>`,
      )
    }

    node
      .querySelectorAll('button[type="submit"],input[type="submit"]')
      .forEach((ele) =>
        ele.addEventListener('click', (e) => {
          if (!node.checkValidity()) {
            e.preventDefault()

            formElements.forEach(setErrorState)
          }
        }),
      )

    formElements.forEach((ele) => {
      ele.addEventListener('input', () => setErrorState(ele))
      ele.addEventListener('blur', () => setErrorState(ele))
      ele.addEventListener('change', () => setErrorState(ele))
    })
  }),
  inputSwitch: SAXX.Init.Component((node) => {
    node.addEventListener('change', () => {
      const id = node.getAttribute('data-target')
      const targets = Array.from(
        document.querySelectorAll(`[data-switch='${id}']`),
      )
      targets.forEach((target) => {
        target.classList.toggle('active', node.checked)
      })
    })
  }),
  modal: SAXX.Init.Component((node, ctx) => {
    const { firstNode } = SAXX.Library.FocusTrap(node)
    const nodeId = node.getAttribute('data-modal-id')

    node.querySelectorAll('[data-modal-close]').forEach((ele) => {
      ele.addEventListener('click', (e) => {
        e.preventDefault()
        ctx.emit(SAXX.Events.ModalControlInteraction)
      })
    })

    ctx.on(
      [
        SAXX.Events.DrawerControlInteraction,
        SAXX.Events.ModalControlInteraction,
      ],
      (state, details) => {
        if (!details?.controlElement || nodeId !== details?.controlElement) {
          node.toggleAttribute('inert', true)
          node.setAttribute('aria-expanded', false)

          state.restoreFocus?.focus({ preventScroll: true })
          return
        }

        const isAlreadyOpen = !!JSON.parse(node.getAttribute('aria-expanded'))
        const shouldFocus = !JSON.parse(node.getAttribute('aria-expanded'))

        node.toggleAttribute('inert', false)
        node.setAttribute('aria-expanded', true)

        if (shouldFocus && !isAlreadyOpen) {
          ctx.hydrate({ restoreFocus: document.activeElement })
          firstNode.focus({ preventScroll: true })
        }
      },
    )

    ctx.on(SAXX.Events.CustomizerSelectEvent, (_, details) => {
      if (node.getAttribute('data-section-id') === details.sectionId) {
        node.toggleAttribute('inert', false)
        node.setAttribute('aria-expanded', true)
      }
    })

    ctx.on(SAXX.Events.CustomizerDeselectEvent, (_, details) => {
      if (node.getAttribute('data-section-id') === details.sectionId) {
        node.toggleAttribute('inert', true)
        node.setAttribute('aria-expanded', false)
      }
    })

    node.addEventListener('keydown', (e) => {
      if (JSON.parse(node.getAttribute('aria-expanded')) === false) return

      const isEscPressed = e.key === 'Escape' || e.keyCode === 27
      if (!isEscPressed) return

      ctx.emit(SAXX.Events.ModalControlInteraction)
    })
  }),
  recommendationCarousel: SAXX.Init.Component((node) => {
    const viewport = node.querySelector('[data-loading-target]')
    const ctaTemplate = node.querySelector('#cta-card-template')

    if (node.getAttribute('data-placement-type') === 'previously-viewed') {
      const sectionID = node.getAttribute('data-section-id')
      const template = document.querySelector(
        `template[data-recommendation-template='${sectionID}']`,
      ).content
      const limit = Number(node.getAttribute('data-limit') || 10)
      const handles = SAXX.Session.PreviouslyViewedProducts.filter(
        (handle) => !window.location.pathname.includes(handle),
      )

      if (!handles.length) {
        node.classList.add('no-placement')
        return
      }

      viewport.innerHTML = ``

      handles.slice(0, limit).forEach((handle) => {
        const individualTemplate = document.importNode(template, true)
        individualTemplate
          .querySelector('nosto-dynamic-card')
          .setAttribute('handle', handle)

        viewport.appendChild(individualTemplate)
      })
    }

    if (node.getAttribute('data-type') === 'curated') {
      if (ctaTemplate) {
        node
          .querySelector('[data-loading-target]')
          .appendChild(ctaTemplate.content.cloneNode(true))
      }

      new SAXX.Library.Carousel(node)
      return
    }

    const url = node.getAttribute('data-url')

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Fetch error! ${res.status}`)
        return res.text()
      })
      .then((response) => {
        const section = new DOMParser().parseFromString(response, 'text/html')
        viewport.innerHTML = section.querySelector(
          '[data-loading-target]',
        ).innerHTML

        if (ctaTemplate) {
          viewport.appendChild(ctaTemplate.content.cloneNode(true))
        }

        new SAXX.Library.Carousel(node)

        SAXX.App.mount()
      })
      .catch((e) => {
        console.error(e)
        node.classList.add('no-placement')
      })
      .finally(() => {
        node.classList.remove('loading')
      })
  }),
  tabs: SAXX.Init.Component((node) => {
    /* This is a modified version of the W3C accessible tab interface scripting found at
     * https://www.w3.org/WAI/ARIA/apg/patterns/tabs/examples/tabs-automatic/
     */

    const tabs = Array.from(node.querySelectorAll('[role=tab]'))
    const tabPanels = []
    let firstTab
    let lastTab

    const setSelectedTab = (currentTab) => {
      tabs.forEach((tab, i) => {
        if (currentTab === tab) {
          tab.setAttribute('aria-selected', 'true')
          tab.removeAttribute('tabindex')
          tab.classList.add('tab__active')
          tab.focus()
          tabPanels[i].classList.add('tab-panel__active')
        } else {
          tab.setAttribute('aria-selected', 'false')
          tab.tabIndex = -1
          tab.classList.remove('tab__active')
          tabPanels[i].classList.remove('tab-panel__active')
        }
      })
    }

    const setSelectedToPreviousTab = (currentTab) => {
      if (currentTab === firstTab) {
        return setSelectedTab(lastTab)
      }
      const index = tabs.indexOf(currentTab)
      setSelectedTab(tabs[index - 1])
    }

    const setSelectedToNextTab = (currentTab) => {
      if (currentTab === lastTab) {
        return setSelectedTab(firstTab)
      }
      const index = tabs.indexOf(currentTab)
      setSelectedTab(tabs[index + 1])
    }

    const onKeyDownEventListener = (event) => {
      const target = event.currentTarget
      let flag = false

      switch (event.key) {
        case 'ArrowLeft':
          setSelectedToPreviousTab(target)
          flag = true
          break
        case 'ArrowRight':
          setSelectedToNextTab(target)
          flag = true
          break
        case 'Home':
          setSelectedTab(firstTab)
          flag = true
          break
        case 'End':
          setSelectedTab(lastTab)
          flag = true
          break
      }

      if (flag) {
        event.stopPropagation()
        event.preventDefault()
      }
    }

    tabs.forEach((tab) => {
      tabPanels.push(document.getElementById(tab.getAttribute('aria-controls')))

      tab.addEventListener('keydown', onKeyDownEventListener)
      tab.addEventListener('click', (event) => {
        setSelectedTab(event.currentTarget)
      })

      if (!firstTab) {
        firstTab = tab
      }
      lastTab = tab
    })
  }),
}

const productCardComponents = {
  productCardAction,
  productCardQuickShop,
  productCardSwatches,
}

const instantSearchComponents = { instantSearch, instantSearchQuery }

SAXX.App = picoapp(SAXX.Init.Components, {
  processingThemeQueue: false,
  themeQueue: [],
})
SAXX.App.add(globalComponents)
SAXX.App.add(productCardComponents)
SAXX.App.add(instantSearchComponents)
SAXX.App.on('error', ({ error }) => {
  if (typeof window.Sentry === 'object') window.Sentry.captureException(error)
})

SAXX.App.on(SAXX.Events.ThemeEnqueue, (state, task) => {
  state.themeQueue.push(task)
  SAXX.App.emit(SAXX.Events.ThemeQueueProcess)
})
SAXX.App.on(SAXX.Events.ThemeQueueProcess, async (state) => {
  if (state.processingThemeQueue || state.themeQueue.length === 0) {
    return
  }

  SAXX.App.hydrate({ processingThemeQueue: true })
  const task = state.themeQueue.shift()

  try {
    await task()
  } catch (e) {
    console.error('Cart Queue Failed', e)
  }

  SAXX.App.hydrate({ processingThemeQueue: false })
  SAXX.App.emit(SAXX.Events.ThemeQueueProcess)
})

SAXX.ThemeInit = () => {
  let hasBeenRun = false

  return () => {
    if (!hasBeenRun) {
      hasBeenRun = true
      SAXX.App.emit(SAXX.Events.ThemeEnqueue, null, () => {
        if (
          window.sessionStorage.getItem('CartCurrency') &&
          window.sessionStorage.getItem('CartCurrency') ===
            Shopify.currency.active
        ) {
          return
        }

        window.sessionStorage.setItem('CartCurrency', Shopify.currency.active)

        window.requestIdleCallback(async () => {
          const cart = await SAXX.Library.FetchCart({
            priority: 'low',
            sections: [],
          })

          if (cart.items.length === 0) {
            return
          }

          SAXX.App.emit('theme:localizedCartUpdate', null, {
            cartUpdating: true,
          })

          const itemsToProcess = new Map()

          cart.items.forEach((item) => {
            itemsToProcess.set(
              `gid://shopify/ProductVariant/${item.variant_id}`,
              item,
            )
          })

          const ids = [...itemsToProcess.keys()]

          const [originalPrices, localePrices] = await Promise.all([
            SAXX.Library.ExecuteGraphQlQuery(
              `query getMultipleVariants($ids: [ID!]!) @inContext(country: ${SAXX.Shop.Country}) {
                nodes(ids: $ids) {
                  ... on ProductVariant {
                    id
                    price {
                      amount
                    }
                    compareAtPrice {
                      amount
                    }
                  }
                }
              }`,
              { ids },
              false,
            ),
            SAXX.Library.ExecuteGraphQlQuery(
              `query getMultipleVariants($ids: [ID!]!) @inContext(country: ${Shopify.country}) {
                nodes(ids: $ids) {
                  ... on ProductVariant {
                    id
                    price {
                      amount
                    }
                    compareAtPrice {
                      amount
                    }
                  }
                }
              }`,
              { ids },
              false,
            ),
          ])

          if (originalPrices?.errors || localePrices?.errors) {
            console.error(
              'Error fetching prices in localized context to update cart data',
            )

            SAXX.App.emit(SAXX.Events.CartUpdatingError)
            return
          }

          const originals = originalPrices?.data?.nodes
          const locales = localePrices?.data?.nodes

          const updates = [...itemsToProcess].map(async ([key, value]) => {
            const originalPrice = originals.find((item) => item?.id === key)
            const localePrice = locales.find((item) => item?.id === key)

            const properties = {
              ...value.properties,
              _ORIGINAL_PRICE_LOCALIZED: SAXX.Library.FormatMoneyAmount(
                localePrice.compareAtPrice?.amount ?? localePrice.price?.amount,
              ),
              _ORIGINAL_PRICE: SAXX.Library.FormatMoneyAmount(
                originalPrice.compareAtPrice?.amount ??
                  originalPrice.price?.amount,
              ),
            }

            if (
              SAXX.Shop.FinalSaleEnabled &&
              Number(localePrice.compareAtPrice?.amount) >
                Number(localePrice.price?.amount)
            ) {
              properties._ASRC = 'Final Sale'
            } else {
              delete properties._ASRC
            }

            return await SAXX.Library.UpdateItemInCart({
              id: value.key,
              quantity: value.quantity,
              properties,
              sections: [],
            })
          })

          Promise.all(updates).finally(async () => {
            const cart = await SAXX.Library.FetchCart()
            const sections = await fetch(
              `${window.Shopify.routes.root}?sections=cart-drawer`,
            ).then((res) => res.json())

            SAXX.App.emit(SAXX.Events.CartUpdated, null, {
              cart: {
                ...cart,
                sections,
              },
            })
          })
        })
      })
    } else {
      console.error('Theme has already been Initialized')
    }
  }
}

SAXX.App.on(SAXX.Events.RecommendationRenderEvent, () => {
  const state = SAXX.App.getState().nostoState ?? {
    filledElements: [],
    unFilledElements: [],
  }

  document.querySelectorAll('[data-placement-id]').forEach((placement) => {
    placement.classList.remove('loading')

    const placementId = placement.getAttribute('data-placement-id')

    placement.classList.toggle(
      'no-placement',
      !state.filledElements.some((ele) => ele === placementId) &&
        !placement.classList.contains('loaded'),
    )
    placement.classList.toggle(
      'loaded',
      state.filledElements.some((ele) => ele === placementId) ||
        placement.classList.contains('loaded'),
    )

    if (placement.classList.contains('loaded')) {
      new SAXX.Library.Carousel(placement, { cell: '[template]' })
    }
  })
})

SAXX.App.on('mount', () => {
  let i
  while (typeof (i = SAXX.Init.Callbacks.shift()) !== 'undefined') i()()

  document
    .querySelectorAll('[data-drawer-trigger]:not(.processed)')
    .forEach((node) => {
      node.classList.add('processed')
      node.addEventListener('click', (e) => {
        e.preventDefault()
        const controlElement = e.target.getAttribute('aria-controls')
        if (document.querySelector(`[data-drawer-id="${controlElement}"`)) {
          e.preventDefault()
          SAXX.App.emit(SAXX.Events.DrawerControlInteraction, null, {
            controlElement,
          })
        }
      })
    })

  document.querySelectorAll('a[href^="#"]:not(.processed)').forEach((link) => {
    link.classList.add('processed')
    link.addEventListener('click', (e) => {
      if (
        window.location.pathname === e.target.pathname &&
        e.target.hash &&
        e.target.hash.length > 0
      ) {
        e.preventDefault()
        window.location.hash = e.target.hash
        SAXX.Library.SmoothScroll(e.target.hash)
      }
    })
  })
})

SAXX.App.mount()

document.addEventListener('shopify:section:load', (e) => {
  SAXX.App.mount()
  SAXX.App.emit(SAXX.Events.CustomizerSelectEvent, null, e.detail)
})
document.addEventListener('shopify:section:reorder', (e) => {
  SAXX.App.mount()
  SAXX.App.emit(SAXX.Events.CustomizerSelectEvent, null, e.detail)
})
document.addEventListener('shopify:section:select', (e) => {
  SAXX.App.emit(SAXX.Events.CustomizerSelectEvent, null, e.detail)
})
document.addEventListener('shopify:section:deselect', (e) => {
  SAXX.App.emit(SAXX.Events.CustomizerDeselectEvent, null, e.detail)
})

document.addEventListener('custom-bundle:added', async (e) => {
  SAXX.App.emit(SAXX.Events.DrawerControlInteraction, null, {
    controlElement: 'slide-out-cart',
    cartUpdating: true,
  })

  SAXX.App.emit(SAXX.Events.CartUpdated, null, {
    cart: e.detail,
    addedLineItems: e.detail.items,
  })
})

document.addEventListener('geolizr.geoData', ({ detail: location }) => {
  SAXX.App.emit(SAXX.Events.UserLocationIsSet, {
    location,
  })
})
